import { Component } from '@verndale/core';
import { breakpoints } from '../helpers/constants';
import anime from 'animejs/lib/anime.es.js';
import Swiper from 'swiper';
/**
 * Refer to https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html
 * for accessibility concerns
 */

class TabbedContentRow extends Component {
  setupDefaults() {
    this.dom = {
      tabsContainer: this.el.querySelector('.tabbed-content-row__tabs'),
      tabsContent: [
        ...this.el.querySelectorAll(
          '.tabbed-content-row__tab-content[role="tabpanel"]'
        )
      ],
      sliders: this.el.querySelectorAll(
        '.tabbed-content-row__slider-container'
      ),
      cards: this.el.querySelectorAll('.cta-description-card')
    };
    this.currentTab = 0;
    this.initializeTabs();

    if (this.dom.sliders.length > 0) {
      const firstSlide = this.dom.sliders[0].querySelector(
        '.tabbed-content__image'
      );
      firstSlide.classList.add('active');
      this.initSlider(this.dom.sliders[0]);
    }
  }

  addListeners() {
    const { cards } = this.dom;

    cards.forEach(card =>
      card.addEventListener('mouseover', ::this.onHoverCard)
    );
    cards.forEach(card =>
      card.addEventListener('mouseleave', ::this.onLeaveCard)
    );
    this.dom.tabs.forEach((tab, index) => {
      // Click event on tab
      tab.addEventListener('click', e => this.handleTabsClick(e, index));
      // Keydown event on tab
      tab.addEventListener('keydown', e => this.handleTabsKeydown(e));
    });
    if (this.dom.sliders.length > 0) {
      window.addEventListener('resize', () => {
        const slider = this.dom.sliders[0];
        const imageContainer = slider.querySelector('.tabbed-content__slide');
        const captionContainer = slider.querySelector(
          '.tabbed-content-row__tab-image-caption'
        );
        const sliderHeight =
          imageContainer.offsetHeight + captionContainer.offsetHeight;
        slider.style.height = `${sliderHeight}px`;
      });
    }
  }

  initializeTabs() {
    if (this.dom.tabsContent.length > 0) {
      this.renderTabs();
      this.dom.tabsContent[0].classList.add('active');
      this.dom.tabsContent[0].setAttribute('tabindex', 0);
      this.el.classList.add('initialized');
    }
  }

  changeSelectedTab(tabIndex) {
    this.currentTab = tabIndex;
    const currentTabContent = this.dom.tabsContent[tabIndex];
    const currentTab = this.dom.tabs[tabIndex];
    // Reset tabsContent styles and accessibility attributes
    this.dom.tabsContent.forEach((tabContent, index) => {
      tabContent.setAttribute('tabindex', -1);
      tabContent.classList.remove('active');
    });
    // Update currentTabContent styles and accessibility attributes
    currentTabContent.setAttribute('tabindex', 0);
    currentTabContent.classList.add('active');
    // Reset tabs styles and accessibility attributes
    this.dom.tabs.forEach(tab => {
      tab.setAttribute('aria-selected', false);
      tab.setAttribute('tabindex', -1);
      tab.classList.remove('active');
    });
    // Update currentTab styles and accessibility attributes
    currentTab.setAttribute('aria-selected', true);
    currentTab.setAttribute('tabindex', 0);
    currentTab.classList.add('active');
    currentTab.focus();
    // Scroll to element on tablet
    if (window.innerWidth <= breakpoints.laptop) {
      this.dom.tabsContainer.scrollTo({
        top: 0,
        left: currentTab.offsetLeft - 20,
        behavior: 'smooth'
      });
    }
  }

  handleTabsClick(e, index) {
    this.changeSelectedTab(index);
    this.removeAnimations();
    if (this.dom.sliders[index]) {
      this.initSlider(this.dom.sliders[index]);
    }
  }

  handleTabsKeydown(e) {
    const keyMapper = { ArrowLeft: -1, ArrowRight: 1 };
    // Return if theres no action for a key
    if (!keyMapper[e.key]) return;
    const nextTabIndex = keyMapper[e.key];
    const lastTabIndex = this.dom.tabs.length - 1;
    // Handle navigation from 0 index to last index
    if (this.currentTab + nextTabIndex === -1) {
      this.changeSelectedTab(lastTabIndex);
      // Handle navigation from last index to 0 index
    } else if (this.currentTab + nextTabIndex > lastTabIndex) {
      this.changeSelectedTab(0);
      // Handle regular navigation
    } else {
      this.changeSelectedTab(this.currentTab + nextTabIndex);
    }
  }

  renderTabs() {
    // Generate tabs
    this.dom.tabsContainer.innerHTML = this.dom.tabsContent
      .filter(tabContent => {
        // Only render tabs if has child nodes
        /*return tabContent.hasChildNodes();*/
        return true;
      })
      .map((tabContent, index) => {
        const tabId = tabContent.getAttribute('aria-labelledby');
        const tabContentId = tabContent.getAttribute('id');
        const tabLabel = tabContent.dataset.tabLabel;
        return `
        <button
          class='${!index ? 'active' : ''}'
          role='tab'
          tabindex='${!index ? 0 : -1}'
          aria-selected='${!index}'
          aria-controls='${tabContentId}'
          id='${tabId}'
        >
          <span>${tabLabel}</span>
        </button>
      `;
      })
      .join('');
    // Set tabs reference
    this.dom.tabs = [
      ...this.el.querySelectorAll(
        '.tabbed-content-row__tabs button[role="tab"]'
      )
    ];
  }

  initSlider(slider) {
    const images = Array.from(
      slider.querySelectorAll('.tabbed-content__image')
    );
    const captions = Array.from(
      slider.querySelectorAll('.tabbed-content-row__tab-image-caption')
    );
    if (
      slider &&
      images.length > 1
    ) {
      const imageContainer = slider.querySelector('.tabbed-content__slide');
      const captionContainer = slider.querySelector(
        '.tabbed-content-row__tab-image-caption'
      );
      slider.style.height = `${
        imageContainer.offsetHeight + captionContainer.offsetHeight
      }px`;

      const progressBars = Array.from(slider.querySelectorAll('.progress-bar'));
      const currentImageIndex = this.getActiveSlide(images);
      const time = 400;
      this.slideAnimation = anime({
        targets: images[currentImageIndex],
        translateX: ['0%', '-100%'],
        easing: 'easeInOutQuad',
        delay: 7000,
        duration: time,
        autoplay: true,
        update: () => {
          progressBars[currentImageIndex].style.width = `${
            this.slideAnimation.progress + 5.4
          }%`;
        },
        begin: () => {
          const currentImageIndex = this.getActiveSlide(images);
          images[currentImageIndex].classList.add('active');
          const currentCaption = captions[currentImageIndex];
          currentCaption?.classList.add(
            'tabbed-content-row__tab-image-caption--active'
          );
          images[
            currentImageIndex === images.length - 1 ? 0 : currentImageIndex + 1
          ].classList.add('next');
        },
        complete: () => {
          const currentImageIndex = this.getActiveSlide(images);
          const currentCaption = captions[currentImageIndex];
          currentCaption?.classList.remove(
            'tabbed-content-row__tab-image-caption--active'
          );
          const oldNext = images.find(image =>
            image.classList.contains('next')
          );
          if (oldNext) oldNext.classList.remove('next');
          images[currentImageIndex].removeAttribute('style');
          images[currentImageIndex].classList.remove('active');
          images[
            currentImageIndex === images.length - 1 ? 0 : currentImageIndex + 1
          ].classList.add('active');
          progressBars[currentImageIndex].style.width = '0';
          this.initSlider(slider);
        }
      });
    } else {
      const currentImageIndex = this.getActiveSlide(images);
      images[currentImageIndex].classList.add('active');
      const currentCaption = captions[currentImageIndex];
      currentCaption?.classList.add(
        'tabbed-content-row__tab-image-caption--active'
      );
    }
  }

  removeAnimations() {
    const animations = anime.running;
    animations.forEach((animation, i) => {
      animation.remove(animation.animatables[i].target);
    });
  }

  getActiveSlide(images) {
    const currentImageIndex = images.findIndex(image =>
      image.classList.contains('active')
    );
    return currentImageIndex === -1 || !currentImageIndex
      ? 0
      : currentImageIndex;
  }

  onHoverCard(e) {
    const { cards } = this.dom;
    cards.forEach(
      card => !card.contains(e.target) && card.classList.add('light-opacity')
    );
  }

  onLeaveCard(e) {
    const { cards } = this.dom;
    cards.forEach(
      card => !card.contains(e.target) && card.classList.remove('light-opacity')
    );
  }
}

export default TabbedContentRow;
